import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SearchEngine from './SearchEngine';
import SevaNetworkBanner from './SevaNetworkBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Sanitize PII (Personally Identifiable Information) from public display
const sanitizePII = (text) => {
  if (!text || typeof text !== 'string') return text;
  let sanitized = text;
  sanitized = sanitized.replace(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g, '[email hidden]');
  sanitized = sanitized.replace(/(\+?\d{1,3}[\s\-]?)?\(?\d{2,5}\)?[\s\-]?\d{3,5}[\s\-]?\d{3,5}/g, (match) => {
    const digits = match.replace(/\D/g, '');
    if (digits.length >= 7 && digits.length <= 15) return '[phone hidden]';
    return match;
  });
  sanitized = sanitized.replace(/whatsapp\s*:?\s*\+?\d[\d\s\-]{7,14}/gi, '[WhatsApp hidden]');
  return sanitized;
};

// ============================================================================
// DAY-OF-WEEK THEME CONFIG
// ============================================================================
const DAY_THEMES = [
  {
    key: 'sunday', className: 'day-theme-sunday',
    graha_en: 'Surya', graha_ta: 'à®šà¯‚à®°à®¿à®¯à®©à¯', graha_symbol: 'â˜€ï¸',
    greeting_en: 'Blessed Sunday â€” Day of Surya (Sun)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®žà®¾à®¯à®¿à®±à¯à®±à¯à®•à¯à®•à®¿à®´à®®à¯ˆ â€” à®šà¯‚à®°à®¿à®¯ à®µà®¾à®°à®®à¯',
    deity_en: 'Lord Surya Narayana', deity_ta: 'à®šà¯‚à®°à®¿à®¯ à®¨à®¾à®°à®¾à®¯à®£à®©à¯',
  },
  {
    key: 'monday', className: 'day-theme-monday',
    graha_en: 'Chandra', graha_ta: 'à®šà®¨à¯à®¤à®¿à®°à®©à¯', graha_symbol: 'ðŸŒ™',
    greeting_en: 'Blessed Monday â€” Day of Chandra (Moon)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®¤à®¿à®™à¯à®•à®Ÿà¯à®•à®¿à®´à®®à¯ˆ â€” à®šà®¨à¯à®¤à®¿à®° à®µà®¾à®°à®®à¯',
    deity_en: 'Lord Shiva', deity_ta: 'à®šà®¿à®µà®ªà¯†à®°à¯à®®à®¾à®©à¯',
  },
  {
    key: 'tuesday', className: 'day-theme-tuesday',
    graha_en: 'Mangala', graha_ta: 'à®šà¯†à®µà¯à®µà®¾à®¯à¯', graha_symbol: 'ðŸ”´',
    greeting_en: 'Blessed Tuesday â€” Day of Mangala (Mars)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®šà¯†à®µà¯à®µà®¾à®¯à¯à®•à¯à®•à®¿à®´à®®à¯ˆ â€” à®šà¯†à®µà¯à®µà®¾à®¯à¯ à®µà®¾à®°à®®à¯',
    deity_en: 'Lord Subrahmanya', deity_ta: 'à®šà¯à®ªà¯à®°à®®à®£à¯à®¯à®°à¯',
  },
  {
    key: 'wednesday', className: 'day-theme-wednesday',
    graha_en: 'Budha', graha_ta: 'à®ªà¯à®¤à®©à¯', graha_symbol: 'ðŸŸ¢',
    greeting_en: 'Blessed Wednesday â€” Day of Budha (Mercury)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®ªà¯à®¤à®©à¯à®•à®¿à®´à®®à¯ˆ â€” à®ªà¯à®¤à®©à¯ à®µà®¾à®°à®®à¯',
    deity_en: 'Lord Vishnu', deity_ta: 'à®¸à¯à®°à¯€à®®à®¨à¯ à®¨à®¾à®°à®¾à®¯à®£à®©à¯',
  },
  {
    key: 'thursday', className: 'day-theme-thursday',
    graha_en: 'Guru', graha_ta: 'à®•à¯à®°à¯', graha_symbol: 'ðŸŸ ',
    greeting_en: 'Blessed Thursday â€” Day of Guru (Jupiter)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®µà®¿à®¯à®¾à®´à®•à¯à®•à®¿à®´à®®à¯ˆ â€” à®•à¯à®°à¯ à®µà®¾à®°à®®à¯',
    deity_en: 'Lord Dakshinamoorthy', deity_ta: 'à®¤à®•à¯à®·à®¿à®£à®¾à®®à¯‚à®°à¯à®¤à¯à®¤à®¿',
  },
  {
    key: 'friday', className: 'day-theme-friday',
    graha_en: 'Shukra', graha_ta: 'à®šà¯à®•à¯à®•à®¿à®°à®©à¯', graha_symbol: 'ðŸŒ¸',
    greeting_en: 'Blessed Friday â€” Day of Shukra (Venus)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®µà¯†à®³à¯à®³à®¿à®•à¯à®•à®¿à®´à®®à¯ˆ â€” à®šà¯à®•à¯à®•à®¿à®° à®µà®¾à®°à®®à¯',
    deity_en: 'Goddess Lakshmi', deity_ta: 'à®¸à¯à®°à¯€ à®®à®¹à®¾à®²à®•à¯à®·à¯à®®à®¿',
  },
  {
    key: 'saturday', className: 'day-theme-saturday',
    graha_en: 'Shani', graha_ta: 'à®šà®©à®¿', graha_symbol: 'ðŸª',
    greeting_en: 'Blessed Saturday â€” Day of Shani (Saturn)',
    greeting_ta: 'à®‡à®©à®¿à®¯ à®šà®©à®¿à®•à¯à®•à®¿à®´à®®à¯ˆ â€” à®šà®©à®¿ à®µà®¾à®°à®®à¯',
    deity_en: 'Lord Hanuman', deity_ta: 'à®†à®žà¯à®šà®¨à¯‡à®¯à®°à¯',
  }
];

const getTodayTheme = () => DAY_THEMES[new Date().getDay()];

// ============================================================================
// CATEGORY SECTIONS CONFIG â€” maps menu headings to content
// ============================================================================
const CATEGORY_SECTIONS = [
  {
    id: 'anmikam',
    icon: 'ðŸ›•',
    title_en: 'Anmikam â€” Spiritual Articles',
    title_ta: 'à®†à®©à¯à®®à®¿à®•à®®à¯ â€” à®†à®©à¯à®®à¯€à®•à®•à¯ à®•à®Ÿà¯à®Ÿà¯à®°à¯ˆà®•à®³à¯',
    desc_en: '108 Divyadesams, Sathya Narayana Pooja, and spiritual articles',
    desc_ta: '108 à®¤à®¿à®µà¯à®¯à®¤à¯‡à®šà®™à¯à®•à®³à¯, à®šà®¤à¯à®¯ à®¨à®¾à®°à®¾à®¯à®£ à®ªà¯‚à®œà¯ˆ, à®†à®©à¯à®®à¯€à®•à®•à¯ à®•à®Ÿà¯à®Ÿà¯à®°à¯ˆà®•à®³à¯',
    link: '/divyadesams',
    viewAllLink: '/blog',
    viewAllText_en: 'All Articles',
    viewAllText_ta: 'à®…à®©à¯ˆà®¤à¯à®¤à¯ à®•à®Ÿà¯à®Ÿà¯à®°à¯ˆà®•à®³à¯',
    images: [
      '/images/rituals/kalyana-uthsavam.jpg',
      '/images/rituals/naandhi.jpg',
      '/images/sathya-narayana.jpg',
    ],
    subLinks: [
      { slug: '/divyadesams', label_en: '108 Divyadesams', label_ta: '108 à®¤à®¿à®µà¯à®¯à®¤à¯‡à®šà®™à¯à®•à®³à¯', icon: 'ðŸ›ï¸' },
      { slug: '/sathya-narayana-pooja', label_en: 'Sathya Narayana Pooja', label_ta: 'à®¸à®¤à¯à®¯ à®¨à®¾à®°à®¾à®¯à®£ à®ªà¯‚à®œà¯ˆ', icon: 'ðŸ™' },
      { slug: '/blog', label_en: 'Anubava Arivu (Articles)', label_ta: 'à®…à®©à¯à®ªà®µ à®…à®±à®¿à®µà¯ (à®•à®Ÿà¯à®Ÿà¯à®°à¯ˆà®•à®³à¯)', icon: 'ðŸ“°' },
    ],
  },
  {
    id: 'astrology',
    icon: 'â­',
    title_en: 'Astrology â€” Jothidam',
    title_ta: 'à®œà¯‹à®¤à®¿à®Ÿà®®à¯',
    desc_en: 'Vakya Casting, Matching, Dasa Bukthi, and daily compatibility',
    desc_ta: 'à®µà®¾à®•à¯à®•à®¿à®¯ à®œà®¾à®¤à®•à®®à¯, à®ªà¯Šà®°à¯à®¤à¯à®¤à®®à¯, à®¤à®šà®¾ à®ªà¯à®•à¯à®¤à®¿, à®¤à®¿à®© à®ªà¯Šà®°à¯à®¤à¯à®¤à®®à¯',
    link: '/services',
    viewAllLink: '/services',
    viewAllText_en: 'All Services',
    viewAllText_ta: 'à®…à®©à¯ˆà®¤à¯à®¤à¯ à®¸à¯‡à®µà¯ˆà®•à®³à¯',
    images: [
      '/images/rituals/Raasi-Mandalam.jpg',
      '/images/porutha-chakram.jpg',
      '/images/family-tree.jpg',
    ],
    subLinks: [
      { slug: '/services', label_en: 'All Services', label_ta: 'à®¸à¯‡à®µà¯ˆà®•à®³à¯', icon: 'ðŸ”®' },
      { slug: '/panchangam/casting', label_en: 'Vakya Casting', label_ta: 'à®µà®¾à®•à¯à®•à®¿à®¯ à®œà®¾à®¤à®•à®®à¯', icon: 'â­' },
      { slug: '/panchangam/matching', label_en: 'Vakya Matching', label_ta: 'à®µà®¾à®•à¯à®•à®¿à®¯ à®ªà¯Šà®°à¯à®¤à¯à®¤à®®à¯', icon: 'ðŸ’‘' },
      { slug: '/panchangam/dasabukthi', label_en: 'Dasa Bukthi', label_ta: 'à®¤à®šà®¾ à®ªà¯à®•à¯à®¤à®¿', icon: 'ðŸ“Š' },
    ],
  },
  {
    id: 'panchangam',
    icon: 'ðŸ“…',
    title_en: 'Panchangam â€” Daily Calendar',
    title_ta: 'à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯ â€” à®¨à®¾à®³à¯à®•à®¾à®Ÿà¯à®Ÿà®¿',
    desc_en: 'Live Panchangam, Muhurthams, Good Days, Horai, and Gowri Panchangam',
    desc_ta: 'à®‡à®©à¯à®±à¯ˆà®¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯, à®®à¯à®•à¯‚à®°à¯à®¤à¯à®¤à®™à¯à®•à®³à¯, à®šà¯à®ª à®¨à®¾à®Ÿà¯à®•à®³à¯, à®¹à¯‹à®°à¯ˆ',
    link: '/panchangam/panchangam',
    viewAllLink: '/panchangam/calendar',
    viewAllText_en: 'Full Calendar',
    viewAllText_ta: 'à®®à¯à®´à¯ à®•à®¾à®²à®£à¯à®Ÿà®°à¯',
    images: [
      '/images/aavani-25.jpg',
      '/images/aadi-25.jpg',
      '/images/punyakalam.jpg',
    ],
    subLinks: [
      { slug: '/Sri_Paranthaman_Panchangam_Interactive_Browser.html', label_en: 'Sri Paranthaman Panchangam (Browser)', label_ta: 'à®¸à¯à®°à¯€ à®ªà®°à®¨à¯à®¤à®¾à®®à®©à¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯', icon: 'ðŸŒŸ' },
      { slug: '/Events-And-Shastra-Rules/', label_en: 'Shastric Rules (6,500+)', label_ta: 'à®šà®¾à®¸à¯à®¤à®¿à®° à®µà®¿à®¤à®¿à®•à®³à¯', icon: 'ðŸ“œ' },
      { slug: '/panchangam/panchangam', label_en: 'Live Panchangam', label_ta: 'à®‡à®©à¯à®±à¯ˆà®¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯', icon: 'ðŸ“…' },
      { slug: '/panchangam/calendar', label_en: 'Calendar', label_ta: 'à®•à®¾à®²à®£à¯à®Ÿà®°à¯', icon: 'ðŸ—“ï¸' },
      { slug: '/panchangam/good-days', label_en: 'Good Days', label_ta: 'à®šà¯à®ª à®¨à®¾à®Ÿà¯à®•à®³à¯', icon: 'âœ…' },
      { slug: '/panchangam/horai', label_en: 'Horai', label_ta: 'à®¹à¯‹à®°à¯ˆ', icon: 'â°' },
      { slug: '/panchangam/gowri-panchangam', label_en: 'Gowri Panchangam', label_ta: 'à®•à¯Œà®°à®¿ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯', icon: 'ðŸŒº' },
    ],
  },
  {
    id: 'sastram',
    icon: 'ðŸ“–',
    title_en: 'Sastram â€” Scriptures',
    title_ta: 'à®šà®¾à®¸à¯à®¤à®¿à®°à®®à¯',
    desc_en: 'Dharma Sastram (701 pages) and Pracheena Q&A â€” ancient wisdom',
    desc_ta: 'à®¤à®°à¯à®® à®šà®¾à®¸à¯à®¤à®¿à®°à®®à¯ (701 à®ªà®•à¯à®•à®™à¯à®•à®³à¯), à®ªà¯à®°à®¾à®šà¯€à®© à®†à®šà®¾à®°à®®à¯',
    link: '/dharma-sastram',
    viewAllLink: '/dharma-sastram',
    viewAllText_en: 'Read Dharma Sastram',
    viewAllText_ta: 'à®¤à®°à¯à®® à®šà®¾à®¸à¯à®¤à®¿à®°à®®à¯ à®ªà®Ÿà®¿à®•à¯à®•',
    images: [
      '/images/rituals/Sankalpam.jpg',
      '/images/rituals/pavitram.jpg',
      '/images/rituals/sthandilam.jpg',
    ],
    subLinks: [
      { slug: '/Events-And-Shastra-Rules/', label_en: 'Shastra Decision Rules (6,500+)', label_ta: 'à®šà®¾à®¸à¯à®¤à®¿à®° à®®à¯à®Ÿà®¿à®µà¯†à®Ÿà¯à®•à¯à®•à¯à®®à¯ à®µà®¿à®¤à®¿à®•à®³à¯', icon: 'ðŸ“œ' },
      { slug: '/Sri_Paranthaman_Panchangam_Interactive_Browser.html', label_en: 'Sri Paranthaman Panchangam', label_ta: 'à®¸à¯à®°à¯€ à®ªà®°à®¨à¯à®¤à®¾à®®à®©à¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯', icon: 'ðŸ“…' },
      { slug: '/dharma-sastram', label_en: 'Dharma Sastram (701 Pages)', label_ta: 'à®¤à®°à¯à®® à®šà®¾à®¸à¯à®¤à®¿à®°à®®à¯ (701 à®ªà®•à¯à®•à®™à¯à®•à®³à¯)', icon: 'ðŸ“–' },
      { slug: '/pracheena-qa', label_en: 'Pracheena QA', label_ta: 'à®ªà¯à®°à®¾à®šà¯€à®© à®†à®šà®¾à®°à®®à¯', icon: 'ðŸ†•' },
    ],
  },
  {
    id: 'vaideekam',
    icon: 'ðŸ“¿',
    title_en: 'Vaideekam â€” Vedic Rituals',
    title_ta: 'à®µà¯ˆà®¤à¯€à®•à®®à¯ â€” à®µà¯ˆà®¤à¯€à®• à®•à®°à¯à®®à®¾à®•à¯à®•à®³à¯',
    desc_en: 'Marriage, Upanayanam, Homam, Shradham, Tarpanam, Theetu, and more',
    desc_ta: 'à®¤à®¿à®°à¯à®®à®£à®®à¯, à®‰à®ªà®¨à®¯à®©à®®à¯, à®¹à¯‹à®®à®®à¯, à®¸à¯à®°à®¾à®¤à¯à®¤à®®à¯, à®¤à®°à¯à®ªà¯à®ªà®£à®®à¯, à®¤à¯€à®Ÿà¯à®Ÿà¯',
    link: '/vaideekam',
    viewAllLink: '/vaideekam',
    viewAllText_en: 'Explore All Rituals',
    viewAllText_ta: 'à®…à®©à¯ˆà®¤à¯à®¤à¯ à®•à®°à¯à®®à®¾à®•à¯à®•à®³à¯',
    images: [
      '/images/rituals/aksharabhyasam.jpg',
      '/images/rituals/agni-kundam.jpg',
      '/images/rituals/namakaranam.jpg',
    ],
    subLinks: [
      { slug: '/vaideekam', label_en: 'Vedic Rituals', label_ta: 'à®µà¯ˆà®¤à¯€à®• à®•à®°à¯à®®à®¾à®•à¯à®•à®³à¯', icon: 'ðŸ“¿' },
      { slug: '/panchangam/gen-sankalpam', label_en: 'General Sankalpam', label_ta: 'à®ªà¯Šà®¤à¯ à®šà®™à¯à®•à®²à¯à®ªà®®à¯', icon: 'ðŸ™' },
      { slug: '/religious-services/shradhdha-service', label_en: 'Shradhdha Service', label_ta: 'à®¸à¯à®°à®¾à®¤à¯à®¤ à®¸à¯‡à®µà¯ˆ', icon: 'ðŸª”' },
      { slug: '/panchangam/shradhdha-thithi', label_en: 'Shradhdha Thithi', label_ta: 'à®¸à¯à®°à®¾à®¤à¯à®¤ à®¤à®¿à®¤à®¿', icon: 'ðŸ“‹' },
      { slug: '/panchangam/sankalpam', label_en: 'Tarpanam Sankalpam', label_ta: 'à®¤à®°à¯à®ªà¯à®ªà®£ à®šà®™à¯à®•à®²à¯à®ªà®®à¯', icon: 'ðŸ’§' },
    ],
  },
  {
    id: 'upakarma',
    icon: 'ðŸ”±',
    title_en: 'Upakarma & Special Rituals',
    title_ta: 'à®‰à®ªà®¾à®•à®°à¯à®®à®¾ & à®šà®¿à®±à®ªà¯à®ªà¯ à®•à®°à¯à®®à®¾à®•à¯à®•à®³à¯',
    desc_en: 'Rig, Yajur, Sama, Bodhayana Upakarma & Gayathri Japam guides',
    desc_ta: 'à®°à®¿à®•à¯, à®¯à®œà¯à®°à¯, à®šà®¾à®®, à®ªà¯‹à®¤à®¾à®¯à®© à®‰à®ªà®¾à®•à®°à¯à®®à®¾ & à®•à®¾à®¯à®¤à¯à®°à®¿ à®œà®ªà®®à¯',
    link: '/yajur-upakarma',
    viewAllLink: '/vaideekam',
    viewAllText_en: 'All Upakarma',
    viewAllText_ta: 'à®…à®©à¯ˆà®¤à¯à®¤à¯ à®‰à®ªà®¾à®•à®°à¯à®®à®¾',
    images: [
      '/images/rituals/surya-namaskaram-s.jpg',
      '/images/rituals/thiruman-kaappu.jpg',
      '/images/rituals/srichurnam-final.jpg',
    ],
    subLinks: [
      { slug: '/rig-upakarma', label_en: 'Rig Upakarma', label_ta: 'à®°à®¿à®•à¯ à®‰à®ªà®¾à®•à®°à¯à®®à®¾', icon: 'ðŸ“œ' },
      { slug: '/yajur-upakarma', label_en: 'Yajur Upakarma', label_ta: 'à®¯à®œà¯à®°à¯ à®‰à®ªà®¾à®•à®°à¯à®®à®¾', icon: 'ðŸ“œ' },
      { slug: '/sama-upakarma', label_en: 'Sama Upakarma', label_ta: 'à®šà®¾à®® à®‰à®ªà®¾à®•à®°à¯à®®à®¾', icon: 'ðŸ“œ' },
      { slug: '/bodhayana-upakarma', label_en: 'Bodhayana Upakarma', label_ta: 'à®ªà¯‹à®¤à®¾à®¯à®© à®‰à®ªà®¾à®•à®°à¯à®®à®¾', icon: 'ðŸ“œ' },
      { slug: '/gayathri-japam', label_en: 'Gayathri Japam', label_ta: 'à®•à®¾à®¯à®¤à¯à®°à®¿ à®œà®ªà®®à¯', icon: 'ðŸ™' },
    ],
  },
];

// ============================================================================
// SCROLL REVEAL HOOK
// ============================================================================
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);
  return ref;
};

// ============================================================================
// CONTENT CARD COMPONENT
// ============================================================================
const ContentCard = ({ image, title, excerpt, date, link, index }) => {
  return (
    <Link
      to={link}
      className={`content-card block animate-fade-in-up stagger-${(index % 4) + 1}`}
      style={{ opacity: 0, textDecoration: 'none' }}
    >
      <div className="overflow-hidden" style={{ height: '180px' }}>
        <img
          src={image}
          alt={title}
          className="card-image"
          onError={(e) => {
            e.target.src = '/images/home-page.jpg';
          }}
          loading="lazy"
        />
      </div>
      <div style={{ padding: '1rem' }}>
        <h3 style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#1f2937',
          marginBottom: '0.5rem',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: 0,
        }}>
          {title}
        </h3>
        {excerpt && (
          <p style={{
            fontSize: '0.85rem',
            color: '#6b7280',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            margin: 0,
          }}>
            {excerpt}
          </p>
        )}
        {date && (
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
            {new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
          </p>
        )}
      </div>
    </Link>
  );
};

// ============================================================================
// CATEGORY SECTION COMPONENT
// ============================================================================
const CategorySection = ({ section, language, posts, isAlternate }) => {
  const ref = useScrollReveal();
  const sectionPosts = posts || [];

  // Merge real blog posts with static image cards if no posts
  const displayItems = sectionPosts.length > 0
    ? sectionPosts.slice(0, 3).map((post, i) => ({
        image: post.featured_image || post.image || section.images[i % section.images.length],
        title: language === 'en' ? (post.title_en || post.title) : (post.title_ta || post.title),
        excerpt: language === 'en' ? (post.excerpt_en || post.excerpt || '') : (post.excerpt_ta || post.excerpt || ''),
        date: post.published_at || post.created_at,
        link: post.slug ? `/blog/${post.slug}` : section.link,
      }))
    : section.images.map((img, i) => ({
        image: img,
        title: language === 'en' ? section.subLinks[i]?.label_en || section.title_en : section.subLinks[i]?.label_ta || section.title_ta,
        excerpt: '',
        date: null,
        link: section.subLinks[i]?.slug || section.link,
      }));

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: `2px solid var(--day-card-border, #e5e7eb)`,
      }}>
        <div>
          <h2 className="section-heading">
            <span style={{ fontSize: '1.8rem' }}>{section.icon}</span>
            {language === 'en' ? section.title_en : section.title_ta}
          </h2>
          <p style={{
            fontSize: '0.9rem',
            color: '#6b7280',
            marginTop: '0.75rem',
            marginBottom: 0,
          }}>
            {language === 'en' ? section.desc_en : section.desc_ta}
          </p>
        </div>
        <Link to={section.viewAllLink} className="view-all-link" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {language === 'en' ? section.viewAllText_en : section.viewAllText_ta} â†’
        </Link>
      </div>

      {/* Content Cards */}
      <div className="home-section-grid">
        {displayItems.map((item, idx) => (
          <ContentCard
            key={idx}
            image={item.image}
            title={item.title}
            excerpt={item.excerpt}
            date={item.date}
            link={item.link}
            index={idx}
          />
        ))}
      </div>

      {/* Quick Sub-links */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginTop: '1.25rem',
      }}>
        {section.subLinks.map((sub, i) => (
          <Link
            key={i}
            to={sub.slug}
            className="glass-card"
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: 'var(--day-text-highlight, #1f2937)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
          >
            <span>{sub.icon}</span>
            {language === 'en' ? sub.label_en : sub.label_ta}
          </Link>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = ({ theme, language }) => {
  return (
    <div
      className="hero-parallax"
      style={{
        background: 'linear-gradient(135deg, #0F5E56 0%, #0D524B 50%, #06403A 100%)',
        padding: '1.5rem 1rem 1.2rem 1rem',
        textAlign: 'center',
        position: 'relative',
        boxShadow: 'inner 0 0 50px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>

        {/* Subtitle */}
        <p
          className="animate-fade-in-up stagger-2"
          style={{
            fontSize: 'clamp(0.78rem, 1.75vw, 1.22rem)',
            color: '#FEF3C7',
            fontWeight: 800,
            marginBottom: '0.2rem',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            opacity: 0,
            textShadow: '0 2px 10px rgba(0,0,0,0.7)',
            letterSpacing: '0.01em',
          }}
        >
          {language === 'en'
            ? 'Vedic, Shastra, Sampradaya, Panchangam Assistance'
            : 'à®µà¯ˆà®¤à¯€à®•, à®šà®¾à®¸à¯à®¤à¯à®°, à®¸à®®à¯à®ªà¯à®°à®¤à®¾à®¯, à®ªà®žà¯à®šà®¾à®™à¯à®•, à®‰à®¤à®µà®¿'}
        </p>
        <p
          className="animate-fade-in-up stagger-2"
          style={{
            fontSize: 'clamp(0.6rem, 1.2vw, 0.9rem)',
            color: '#D4AF37',
            fontWeight: 400,
            marginBottom: '0.8rem',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            opacity: 0,
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
            letterSpacing: '0.03em',
            fontStyle: 'italic',
          }}
        >
          {language === 'en'
            ? 'Authentic Information â€” 40 Years of Experience'
            : 'à®†à®¤à®¾à®° à®ªà¯‚à®°à¯à®µà®®à®¾à®© à®¤à®•à®µà®²à¯à®•à®³à¯ â€” 40 à®µà®°à¯à®Ÿ à®…à®©à¯à®ªà®µà®®à¯'}
        </p>

        {/* Quick action buttons & Inline Search - Single Line No Wrap */}
        <div
          className="animate-fade-in-up stagger-3"
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'clamp(0.4rem, 1vw, 0.75rem)',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            paddingBottom: '4px',
            opacity: 0,
            position: 'relative',
            zIndex: 50,
          }}
        >
          <Link
            to="/vaideekam"
            style={{
              padding: '0.5rem clamp(0.6rem, 1.2vw, 1.2rem)',
              background: '#8B4513',
              color: 'white',
              fontWeight: 700,
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: 'clamp(0.78rem, 1.1vw, 0.9rem)',
              whiteSpace: 'nowrap',
              height: '42px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(139,69,19,0.25)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {language === 'en' ? 'ðŸ“¿ Explore Rituals' : 'ðŸ“¿ à®•à®°à¯à®®à®¾à®•à¯à®•à®³à¯'}
          </Link>
          <Link
            to="/panchangam/panchangam"
            style={{
              padding: '0.5rem clamp(0.6rem, 1.2vw, 1.2rem)',
              background: '#2D5A1E',
              color: 'white',
              fontWeight: 700,
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: 'clamp(0.78rem, 1.1vw, 0.9rem)',
              whiteSpace: 'nowrap',
              height: '42px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(45,90,30,0.25)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {language === 'en' ? "ðŸ“… Today's Panchangam" : 'ðŸ“… à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯'}
          </Link>
          <Link
            to="/qa"
            style={{
              padding: '0.5rem clamp(0.6rem, 1.2vw, 1.2rem)',
              background: '#CC6600',
              color: 'white',
              fontWeight: 700,
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: 'clamp(0.78rem, 1.1vw, 0.9rem)',
              whiteSpace: 'nowrap',
              height: '42px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(204,102,0,0.25)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {language === 'en' ? 'â“ Ask a Question' : 'â“ à®•à¯‡à®³à¯à®µà®¿'}
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// QUICK STATS BAR
// ============================================================================
const QuickStatsBar = ({ publicStats, qaStats, language }) => {
  const ref = useScrollReveal();
  if (!publicStats) return null;

  const stats = [
    {
      value: publicStats.total_qa?.toLocaleString() || qaStats.total,
      label_en: 'Q&A Answered',
      label_ta: 'à®•à¯‡à®³à¯à®µà®¿-à®ªà®¤à®¿à®²à¯à®•à®³à¯',
      icon: 'ðŸ’¬',
      link: '/qa',
    },
    {
      value: publicStats.total_posts?.toLocaleString() || '200+',
      label_en: 'Articles',
      label_ta: 'à®•à®Ÿà¯à®Ÿà¯à®°à¯ˆà®•à®³à¯',
      icon: 'ðŸ“°',
      link: '/blog',
    },
    {
      value: publicStats.total_comments?.toLocaleString() || '0',
      label_en: 'Comments',
      label_ta: 'à®‰à®°à¯ˆà®¯à®¾à®Ÿà®²à¯à®•à®³à¯',
      icon: 'ðŸ—¨ï¸',
      link: null,
    },
    {
      value: publicStats.active_users || '0',
      label_en: 'Online Now',
      label_ta: 'à®¤à®±à¯à®ªà¯‹à®¤à¯ à®¤à®³à®¤à¯à®¤à®¿à®²à¯',
      icon: 'ðŸŸ¢',
      link: null,
      pulse: true,
    },
  ];

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '0.5rem 0' }}>
      <div className="glass-card" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        maxWidth: '900px',
        margin: '0 auto',
      }}>
        {stats.map((stat, i) => {
          const inner = (
            <div
              key={i}
              style={{
                textAlign: 'center',
                padding: '0.5rem',
                borderRight: i < 3 ? '1px solid var(--day-card-border, #e5e7eb)' : 'none',
              }}
            >
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{stat.icon}</div>
              <div style={{
                fontSize: 'clamp(1.3rem, 3vw, 2rem)',
                fontWeight: 300,
                color: 'var(--day-text-highlight, #1f2937)',
                letterSpacing: '-0.02em',
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}>
                {language === 'en' ? stat.label_en : stat.label_ta}
              </div>
            </div>
          );
          return stat.link
            ? <Link key={i} to={stat.link} style={{ textDecoration: 'none' }}>{inner}</Link>
            : <div key={i}>{inner}</div>;
        })}
      </div>
    </div>
  );
};

// ============================================================================
// QA HIGHLIGHT SECTION
// ============================================================================
const QAHighlightSection = ({ recentQa, qaStats, language, navigate }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--day-card-border, #e5e7eb)',
      }}>
        <h2 className="section-heading">
          <span style={{ fontSize: '1.8rem' }}>ðŸ’¬</span>
          {language === 'en' ? 'Live Q&A Board' : 'à®•à¯‡à®³à¯à®µà®¿-à®ªà®¤à®¿à®²à¯ à®ªà®•à¯à®¤à®¿'}
        </h2>
        <Link to="/qa" className="view-all-link">
          {language === 'en' ? 'Ask / View All' : 'à®•à¯‡à®³à¯à®µà®¿ à®•à¯‡à®Ÿà¯à®•'} â†’
        </Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
        {/* Stats card */}
        <div className="glass-card" style={{
          padding: '2rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '3rem',
            fontWeight: 200,
            color: 'var(--day-primary, #E67E22)',
            lineHeight: 1,
          }}>
            {qaStats.total}
          </div>
          <div style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginTop: '0.5rem',
          }}>
            {language === 'en' ? 'Questions Answered' : 'à®ªà®¤à®¿à®²à¯à®•à®³à¯ à®…à®³à®¿à®•à¯à®•à®ªà¯à®ªà®Ÿà¯à®Ÿà®©'}
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1rem',
            padding: '0.35rem 1rem',
            background: '#ecfdf5',
            borderRadius: '2rem',
            fontSize: '0.8rem',
            fontWeight: 700,
            color: '#059669',
          }}>
            <span style={{ animation: 'pulseGlow 2s infinite' }}>â—</span>
            {language === 'en' ? 'Replies within 2-24 hours' : '2-24 à®®à®£à®¿ à®¨à¯‡à®°à®¤à¯à®¤à®¿à®²à¯ à®ªà®¤à®¿à®²à¯'}
          </div>
          <Link
            to="/qa"
            style={{
              marginTop: '1.5rem',
              padding: '0.7rem 2rem',
              background: 'var(--day-primary, #E67E22)',
              color: 'white',
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              display: 'inline-block',
            }}
          >
            {language === 'en' ? 'Ask Now' : 'à®•à¯‡à®³à¯à®µà®¿ à®•à¯‡à®³à¯à®™à¯à®•à®³à¯'}
          </Link>
        </div>

        {/* Recent questions */}
        {recentQa.slice(0, 4).map((q, idx) => (
          <div
            key={idx}
            className="content-card"
            style={{ cursor: 'pointer', textDecoration: 'none' }}
            onClick={() => navigate('/qa')}
          >
            <div style={{ padding: '1.25rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
              }}>
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  padding: '0.2rem 0.6rem',
                  borderRadius: '1rem',
                  background: q.status === 'answered' ? '#dcfce7' : '#fef9c3',
                  color: q.status === 'answered' ? '#166534' : '#854d0e',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {q.status === 'answered' ? 'âœ“ Answered' : 'â³ Pending'}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                  {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}
                </span>
              </div>
              <h3 style={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: '0.4rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                padding: 0,
              }}>
                {q.subject || q.title || 'Question'}
              </h3>
              <p style={{
                fontSize: '0.82rem',
                color: '#6b7280',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                margin: 0,
              }}>
                {sanitizePII(q.question) || ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// YOUTUBE SECTION
// ============================================================================
const YouTubeSection = ({ language }) => {
  const ref = useScrollReveal();
  const videoIds = ['GqpihbLnuD0', '6jIdlayX2Sc', 'F-5FX7kev_I', 'zkFGrk-K4iw', 'RZnodcZD8hI', 'XgSmzbAGqrk', '6p1KyvRzQ34'];

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--day-card-border, #e5e7eb)',
      }}>
        <h2 className="section-heading">
          <span style={{ fontSize: '1.8rem' }}>ðŸŽ¬</span>
          {language === 'en' ? 'Ahobilam Vaideekam â€” YouTube' : 'à®…à®¹à¯‹à®ªà®¿à®²à®®à¯ à®µà¯ˆà®¤à¯€à®•à®®à¯ â€” à®¯à¯‚à®Ÿà®¿à®¯à¯‚à®ªà¯'}
        </h2>
        <a
          href="https://www.youtube.com/vaideekam"
          target="_blank"
          rel="noopener noreferrer"
          className="view-all-link"
        >
          Subscribe â†’
        </a>
      </div>

      <div className="horizontal-scroll">
        {videoIds.map((videoId, index) => (
          <div
            key={index}
            style={{
              width: '320px',
              borderRadius: '1rem',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              background: '#000',
              flexShrink: 0,
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0`}
              title={`Ahobilam Vaideekam Video ${index + 1}`}
              style={{ width: '320px', height: '180px', border: 'none', display: 'block' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PHOTO GALLERY (enhanced)
// ============================================================================
const PhotoGallery = ({ language }) => {
  const ref = useScrollReveal();

  const galleryImages = [
    { url: '/images/rituals/kalyana-uthsavam.jpg', title_en: 'Traditional Wedding Ceremony', title_ta: 'à®ªà®¾à®°à®®à¯à®ªà®°à®¿à®¯ à®¤à®¿à®°à¯à®®à®£ à®µà®¿à®´à®¾' },
    { url: '/images/rituals/aksharabhyasam.jpg', title_en: 'Aksharabhyasam', title_ta: 'à®…à®•à¯à®·à®°à®¾à®ªà¯à®¯à®¾à®šà®®à¯' },
    { url: '/images/rituals/agni-kundam.jpg', title_en: 'Sacred Homam', title_ta: 'à®ªà¯à®©à®¿à®¤ à®¹à¯‹à®®à®®à¯' },
    { url: '/images/rituals/namakaranam.jpg', title_en: 'Namakaranam', title_ta: 'à®¨à®¾à®®à®•à®°à®£à®®à¯' },
    { url: '/images/rituals/naandhi.jpg', title_en: 'Naandhi Ritual', title_ta: 'à®¨à®¾à®¨à¯à®¤à®¿ à®šà®Ÿà®™à¯à®•à¯' },
    { url: '/images/rituals/pavitram.jpg', title_en: 'Pavitram', title_ta: 'à®ªà®µà®¿à®¤à¯à®°à®®à¯' },
    { url: '/images/sathya-narayana.jpg', title_en: 'Sathya Narayana Pooja', title_ta: 'à®šà®¤à¯à®¯ à®¨à®¾à®°à®¾à®¯à®£ à®ªà¯‚à®œà¯ˆ' },
    { url: '/images/rituals/vivaha-list.jpg', title_en: 'Vivaha Preparations', title_ta: 'à®µà®¿à®µà®¾à®¹ à®à®±à¯à®ªà®¾à®Ÿà¯à®•à®³à¯' },
  ];

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2.5rem 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--day-card-border, #e5e7eb)',
      }}>
        <h2 className="section-heading">
          <span style={{ fontSize: '1.8rem' }}>ðŸ“¸</span>
          {language === 'en' ? 'Photo Gallery' : 'à®ªà¯à®•à¯ˆà®ªà¯à®ªà®Ÿ à®¤à¯Šà®•à¯à®ªà¯à®ªà¯'}
        </h2>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '0.75rem',
      }}>
        {galleryImages.map((image, index) => (
          <div
            key={index}
            className={`animate-scale-in stagger-${(index % 4) + 1}`}
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '0.75rem',
              aspectRatio: '1/1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              cursor: 'pointer',
              opacity: 0,
            }}
          >
            <img
              src={image.url}
              alt={language === 'en' ? image.title_en : image.title_ta}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.5s ease',
              }}
              loading="lazy"
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(40,15,5,0.75) 0%, transparent 60%)',
              opacity: 0,
              transition: 'opacity 0.3s ease',
              display: 'flex',
              alignItems: 'flex-end',
              padding: '0.75rem',
            }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
              onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
            >
              <p style={{
                color: 'white',
                fontWeight: 600,
                fontSize: '0.85rem',
                margin: 0,
              }}>
                {language === 'en' ? image.title_en : image.title_ta}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// ABOUT SECTION
// ============================================================================
const AboutSection = ({ language }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '3rem 0' }}>
      <div className="glass-card" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '3rem 2rem',
        textAlign: 'center',
      }}>
        <h2 className="section-heading" style={{
          justifyContent: 'center',
          fontSize: '1.8rem',
          marginBottom: '1rem',
        }}>
          {language === 'en' ? 'About Ahobilam.com' : 'à®…à®¹à¯‹à®ªà®¿à®²à®®à¯.à®•à®¾à®®à¯ à®ªà®±à¯à®±à®¿'}
        </h2>
        <p style={{
          fontSize: '1.05rem',
          color: '#4b5563',
          lineHeight: 1.8,
          maxWidth: '600px',
          margin: '1rem auto 0',
        }}>
          {language === 'en'
            ? 'Ahobilam.com has been serving the Sri Vaishnava community since August 19, 1989, providing authentic information about Vedic rituals, mantras, and procedures in Sanskrit, Tamil, and English. Our mission is to preserve and share traditional knowledge with everyone, completely free of cost.'
            : 'à®…à®¹à¯‹à®ªà®¿à®²à®®à¯.à®•à®¾à®®à¯ 1989 à®†à®•à®¸à¯à®Ÿà¯ 19 à®†à®®à¯ à®¤à¯‡à®¤à®¿ à®®à¯à®¤à®²à¯ à®¸à¯à®°à¯€ à®µà¯ˆà®·à¯à®£à®µ à®šà®®à¯‚à®•à®¤à¯à®¤à®¿à®±à¯à®•à¯ à®šà¯‡à®µà¯ˆ à®šà¯†à®¯à¯à®¤à¯ à®µà®°à¯à®•à®¿à®±à®¤à¯, à®µà¯ˆà®¤à¯€à®• à®•à®°à¯à®®à®¾à®•à¯à®•à®³à¯, à®®à®¨à¯à®¤à®¿à®°à®™à¯à®•à®³à¯ à®®à®±à¯à®±à¯à®®à¯ à®¨à®Ÿà¯ˆà®®à¯à®±à¯ˆà®•à®³à¯ à®ªà®±à¯à®±à®¿à®¯ à®‰à®£à¯à®®à¯ˆà®¯à®¾à®© à®¤à®•à®µà®²à¯à®•à®³à¯ˆ à®šà®®à®¸à¯à®•à®¿à®°à¯à®¤à®®à¯, à®¤à®®à®¿à®´à¯ à®®à®±à¯à®±à¯à®®à¯ à®†à®™à¯à®•à®¿à®²à®¤à¯à®¤à®¿à®²à¯ à®µà®´à®™à¯à®•à¯à®•à®¿à®±à®¤à¯.'}
        </p>
      </div>
    </div>
  );
};

// ============================================================================

// ============================================================================
// PREMIER FEATURED SHOWCASE COMPONENT (Panchangam & Shastra Rules)
// ============================================================================
const PremierFeaturedShowcase = ({ language }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '2rem 0 1rem 0' }}>
      {/* Section Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: '#e8f5e9',
          color: '#1b5e20',
          padding: '4px 14px',
          borderRadius: '20px',
          fontSize: '12.5px',
          fontWeight: '800',
          border: '1px solid #c8e6c9',
          marginBottom: '8px'
        }}>
          <span>âœ¨</span>
          <span>{language === 'en' ? 'Ahobilam Special Features' : 'à®…à®¹à¯‹à®ªà®¿à®²à®®à¯ à®šà®¿à®±à®ªà¯à®ªà¯ à®µà¯†à®³à®¿à®¯à¯€à®Ÿà¯à®•à®³à¯'}</span>
        </div>
        <h2 style={{
          fontSize: 'clamp(1.3rem, 2.5vw, 1.85rem)',
          fontWeight: 800,
          color: '#123d16',
          letterSpacing: '-0.01em',
          margin: 0,
        }}>
          {language === 'en'
            ? 'ðŸŒŸ Sri Paranthaman Panchangam & Shastra Decision Rules'
            : 'ðŸŒŸ à®¸à¯à®°à¯€ à®ªà®°à®¨à¯à®¤à®¾à®®à®©à¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯ & à®šà®¾à®¸à¯à®¤à®¿à®° à®µà®¿à®šà¯‡à®· à®®à¯à®Ÿà®¿à®µà¯†à®Ÿà¯à®•à¯à®•à¯à®®à¯ à®µà®¿à®¤à®¿à®•à®³à¯'}
        </h2>
        <p style={{
          fontSize: '0.9rem',
          color: '#4e7352',
          marginTop: '0.4rem',
          marginBottom: 0,
        }}>
          {language === 'en'
            ? 'Authentic 2-Year Interactive Landscape Panchangam & 6,500+ Shastric Decision Rules Catalog'
            : '2026â€“2028 à®¨à¯‡à®°à®Ÿà®¿ à®µà®¾à®•à¯à®•à®¿à®¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯ à®®à®±à¯à®±à¯à®®à¯ 6,500+ à®µà®¿à®šà¯‡à®·à®™à¯à®•à®³à¯à®•à¯à®•à®¾à®© à®šà®¾à®¸à¯à®¤à®¿à®° à®†à®¤à®¾à®° à®µà®¿à®¤à®¿à®•à®³à¯'}
        </p>
      </div>

      {/* Two Premium Preview Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1.5rem',
      }}>

        {/* CARD 1: SRI PARANTHAMAN PANCHANGAM BROWSER */}
        <div className="glass-card" style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #f4faf4 100%)',
          border: '2px solid #2e7d32',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 8px 24px rgba(27, 94, 32, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(27, 94, 32, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(27, 94, 32, 0.12)';
        }}
        >
          {/* Top Banner Stripe */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #1b5e20, #d4af37, #1b5e20)'
          }}></div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{
                background: '#fff8e1',
                color: '#8d6e00',
                border: '1.2px solid #ffe082',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11.5px',
                fontWeight: '800'
              }}>
                ðŸ“… 2026â€“2028 (à®ªà®°à®¾à®ªà®µ â€¢ à®ªà®¿à®²à®µà®™à¯à®•)
              </span>
              <span style={{ fontSize: '26px' }}>ðŸ•‰ï¸</span>
            </div>

            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 800,
              color: '#123d16',
              marginBottom: '8px',
              lineHeight: 1.3
            }}>
              {language === 'en'
                ? 'Sri Paranthaman Panchangam (Interactive Browser)'
                : 'à®¸à¯à®°à¯€ à®ªà®°à®¨à¯à®¤à®¾à®®à®©à¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯ (à®¨à¯‡à®°à®Ÿà®¿ à®µà®¾à®•à¯à®•à®¿à®¯ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯)'}
            </h3>

            <p style={{
              fontSize: '0.86rem',
              color: '#2e5b33',
              lineHeight: 1.5,
              marginBottom: '14px'
            }}>
              {language === 'en'
                ? 'Full 12-month landscape panchangam view. Hover over any event to see exact Shastric decision rules and time windows. Print monthly pages in high-resolution A4 landscape.'
                : 'à®®à®¾à®¤à®µà®¾à®°à®¿à®¯à®¾à®• 12 à®®à®¾à®¤ à®®à¯à®´à¯ à®²à¯‡à®£à¯à®Ÿà¯à®¸à¯à®•à¯‡à®ªà¯ à®ªà®žà¯à®šà®¾à®™à¯à®• à®…à®Ÿà¯à®Ÿà®µà®£à¯ˆ. à®µà®¿à®šà¯‡à®·à®™à¯à®•à®³à®¿à®©à¯ à®®à¯€à®¤à¯ à®®à®µà¯à®¸à¯ à®µà¯ˆà®¤à¯à®¤à®¾à®²à¯ à®®à¯à®´à¯à®®à¯ˆà®¯à®¾à®© à®šà®¾à®¸à¯à®¤à®¿à®° à®®à¯à®Ÿà®¿à®µà¯†à®Ÿà¯à®•à¯à®•à¯à®®à¯ à®µà®¿à®¤à®¿ à®ªà®¾à®ªà¯à®…à®ªà¯. A4 à®…à®šà¯à®šà¯ à®µà®šà®¤à®¿.'}
            </p>

            <div style={{
              background: '#edf7ee',
              border: '1px dashed #a5d6a7',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '16px',
              fontSize: '12px',
              color: '#1b5e20',
              lineHeight: 1.6
            }}>
              <div>âœ“ <b>à®¤à®¿à®©à®šà®°à®¿ à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯:</b> à®¤à®¿à®¤à®¿, à®¨à®•à¯à®·à®¤à¯à®¤à®¿à®°à®®à¯, à®¯à¯‹à®•à®®à¯, à®•à®°à®£à®®à¯, à®šà¯à®°à®¾à®¤à¯à®¤ à®¤à®¿à®¤à®¿</div>
              <div>âœ“ <b>à®šà®¾à®¸à¯à®¤à®¿à®° à®µà®¿à®¤à®¿ à®ªà®¾à®ªà¯à®…à®ªà¯:</b> à®‰à®¤à®¯ / à®ªà®¿à®°à®¤à¯‹à®· / à®¨à®¿à®šà¯€à®¤ à®µà®¿à®¯à®¾à®ªà¯à®¤à®¿ à®®à¯à®Ÿà®¿à®µà¯à®•à®³à¯</div>
              <div>âœ“ <b>A4 à®…à®šà¯à®šà¯:</b> à®’à®±à¯à®±à¯ˆ à®®à®¾à®¤à®®à¯ à®…à®²à¯à®²à®¤à¯ 12 à®®à®¾à®¤ à®ªà¯à®¤à¯à®¤à®•à®®à®¾à®• à®…à®šà¯à®šà®¿à®Ÿà¯à®®à¯ à®µà®šà®¤à®¿</div>
            </div>
          </div>

          <a
            href="/Sri_Paranthaman_Panchangam_Interactive_Browser.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'linear-gradient(135deg, #1b5e20 0%, #0d3810 100%)',
              color: '#ffffff',
              border: '1.5px solid #d4af37',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '13.5px',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(27, 94, 32, 0.25)',
              transition: 'all 0.2s ease',
            }}
          >
            <span>{language === 'en' ? 'Open Panchangam Browser' : 'à®ªà®žà¯à®šà®¾à®™à¯à®•à®®à¯ à®¨à¯‡à®°à®Ÿà®¿ à®ªà®¾à®°à¯à®µà¯ˆ à®¤à®¿à®±à®•à¯à®•'}</span>
            <span>â†—</span>
          </a>
        </div>

        {/* CARD 2: EVENTS & SHASTRA RULES ENGINE */}
        <div className="glass-card" style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #fffdf4 100%)',
          border: '2px solid #d4af37',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 8px 24px rgba(184, 134, 11, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(184, 134, 11, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(184, 134, 11, 0.12)';
        }}
        >
          {/* Top Banner Stripe */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #d4af37, #1b5e20, #d4af37)'
          }}></div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{
                background: '#e8f5e9',
                color: '#1b5e20',
                border: '1.2px solid #a5d6a7',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11.5px',
                fontWeight: '800'
              }}>
                ðŸ“œ 6,500+ à®šà®¾à®¸à¯à®¤à®¿à®° à®®à¯à®Ÿà®¿à®µà¯†à®Ÿà¯à®•à¯à®•à¯à®®à¯ à®µà®¿à®¤à®¿à®•à®³à¯
              </span>
              <span style={{ fontSize: '26px' }}>âš–ï¸</span>
            </div>

            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 800,
              color: '#7a5a00',
              marginBottom: '8px',
              lineHeight: 1.3
            }}>
              {language === 'en'
                ? 'Events & Shastra Decision Rules (6,500+ Rules Engine)'
                : 'à®ªà®žà¯à®šà®¾à®™à¯à®• à®šà®¾à®¸à¯à®¤à®¿à®° à®µà®¿à®šà¯‡à®·à®™à¯à®•à®³à¯ & à®®à¯à®Ÿà®¿à®µà¯†à®Ÿà¯à®•à¯à®•à¯à®®à¯ à®µà®¿à®¤à®¿à®•à®³à¯'}
            </h3>

            <p style={{
              fontSize: '0.86rem',
              color: '#54410a',
              lineHeight: 1.5,
              marginBottom: '14px'
            }}>
              {language === 'en'
                ? 'Exhaustive searchable catalog of 6,500+ events across all 12 months with Nirnaya Sindhu, Dharma Shastram, and Vaidyanatha Dikshitiyam rules.'
                : 'à®¨à®¿à®°à¯à®£à®¯ à®¸à®¿à®¨à¯à®¤à¯, à®¤à®°à¯à®® à®šà®¾à®¸à¯à®¤à®¿à®°à®®à¯ à®®à®±à¯à®±à¯à®®à¯ à®µà¯ˆà®¤à¯à®¯à®¨à®¾à®¤ à®¤à¯€à®•à¯à®·à®¿à®¤à¯€à®¯à®®à¯ à®†à®¤à®¾à®°à®™à¯à®•à®³à¯à®Ÿà®©à¯ 12 à®®à®¾à®¤à®™à¯à®•à®³à®¿à®©à¯ à®…à®©à¯ˆà®¤à¯à®¤à¯ à®µà®¿à®šà¯‡à®·à®™à¯à®•à®³à¯ à®®à®±à¯à®±à¯à®®à¯ à®µà®¿à®°à®¤à®™à¯à®•à®³à¯à®•à¯à®•à®¾à®© à®šà®¾à®¸à¯à®¤à®¿à®° à®¤à¯€à®°à¯à®ªà¯à®ªà¯à®•à¯ à®•à®³à®žà¯à®šà®¿à®¯à®®à¯.'}
            </p>

            <div style={{
              background: '#fff9e6',
              border: '1px dashed #ffe082',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '16px',
              fontSize: '12px',
              color: '#8c5b00',
              lineHeight: 1.6
            }}>
              <div>âœ“ <b>à®‰à®Ÿà®©à®Ÿà®¿ à®¤à¯‡à®Ÿà®²à¯:</b> 12 à®®à®¾à®¤à®™à¯à®•à®³à®¿à®©à¯ à®µà®¿à®šà¯‡à®·à®™à¯à®•à®³à¯, à®†à®´à¯à®µà®¾à®°à¯ à®†à®šà®¾à®°à¯à®¯ à®¤à®¿à®°à¯à®¨à®•à¯à®·à®¤à¯à®¤à®¿à®°à®™à¯à®•à®³à¯</div>
              <div>âœ“ <b>à®šà®¾à®¸à¯à®¤à®¿à®° à®µà®¿à®¯à®¾à®ªà¯à®¤à®¿:</b> à®‰à®¤à¯à®¤à®® à®•à®¾à®² à®…à®³à®µà¯, à®‡à®°à¯ à®¤à®¿à®© à®µà®¿à®¯à®¾à®ªà¯à®¤à®¿/à®…à®µà®¿à®¯à®¾à®ªà¯à®¤à®¿ à®µà®¿à®¤à®¿à®•à®³à¯</div>
              <div>âœ“ <b>à®ªà®¿à®°à®®à®¾à®£ à®šà¯à®²à¯‹à®•à®™à¯à®•à®³à¯:</b> à®®à¯‚à®² à®šà®®à®¸à¯à®•à®¿à®°à¯à®¤ à®ªà®¿à®°à®®à®¾à®£ à®šà¯à®²à¯‹à®•à®®à¯ à®®à®±à¯à®±à¯à®®à¯ à®¤à®®à®¿à®´à¯ à®µà®¿à®³à®•à¯à®•à®®à¯</div>
            </div>
          </div>

          <a
            href="/Events-And-Shastra-Rules/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #a37c00 100%)',
              color: '#123d16',
              border: '1.5px solid #ffffff',
              padding: '10px 16px',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '13.5px',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(184, 134, 11, 0.25)',
              transition: 'all 0.2s ease',
            }}
          >
            <span>{language === 'en' ? 'Explore Shastra Rules Engine' : 'à®šà®¾à®¸à¯à®¤à®¿à®° à®µà®¿à®¤à®¿à®•à®³à¯ à®•à®³à®žà¯à®šà®¿à®¯à®¤à¯à®¤à¯ˆà®¤à¯ à®¤à®¿à®±à®•à¯à®•'}</span>
            <span>â†—</span>
          </a>
        </div>

        {/* CARD 3: MAHALAYA QA KNOWLEDGE BASE */}
        <div className="glass-card" style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #fffbf4 100%)',
          border: '2px solid #e65100',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 8px 24px rgba(230, 81, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(230, 81, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(230, 81, 0, 0.12)';
        }}
        >
          {/* Top Banner Stripe */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '6px',
            background: 'linear-gradient(90deg, #e65100, #ffb300, #e65100)'
          }}></div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{
                background: '#fff3e0', color: '#e65100', border: '1.2px solid #ffcc80',
                padding: '3px 10px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '800'
              }}>
                📖 38,000+ Q&A Posts
              </span>
              <span style={{ fontSize: '26px' }}>📚</span>
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#bf360c', marginBottom: '8px', lineHeight: 1.3 }}>
              {language === 'en' ? 'Mahalaya & Tarpanam Knowledge Base' : 'மஹாலய & தர்ப்பண கேள்வி-பதில் களஞ்சியம்'}
            </h3>
            <p style={{ fontSize: '0.86rem', color: '#5d4037', lineHeight: 1.5, marginBottom: '14px' }}>
              {language === 'en' ? 'Authentic Vaideeka Q&A extracted from Nirnaya Sindhu and 20 years of forum discussions.' : 'நிர்ணய சிந்து மற்றும் 20 வருட தர்மசாஸ்த்ர மன்ற கேள்வி-பதில்களின் தொகுப்பு.'}
            </p>
            <div style={{ background: '#fbe9e7', border: '1px dashed #ffab91', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#d84315', lineHeight: 1.6 }}>
              <div>✓ <b>தர்ப்பண விதிகள்:</b> அமாவாசை, மஹாலய தர்ப்பணங்கள்</div>
              <div>✓ <b>முரண்பாடுகள்:</b> சாஸ்த்ர பேதங்களுக்கு தெளிவான தீர்வுகள்</div>
            </div>
          </div>
          <a href="/mahalaya-shastra-qa" style={{
            background: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)', color: '#ffffff',
            border: '1.5px solid #ffcc80', padding: '10px 16px', borderRadius: '10px', fontWeight: 800,
            fontSize: '13.5px', textDecoration: 'none', textAlign: 'center', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(230, 81, 0, 0.25)',
            transition: 'all 0.2s ease',
          }}>
            <span>{language === 'en' ? 'Browse Q&A Database' : 'கேள்வி-பதில்களைப் படிக்க'}</span>
            <span>↗</span>
          </a>
        </div>

        {/* CARD 4: VADHYAR REGISTRATION */}
        <div className="glass-card" style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #f4f6ff 100%)',
          border: '2px solid #283593',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 8px 24px rgba(40, 53, 147, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(40, 53, 147, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(40, 53, 147, 0.12)';
        }}
        >
          {/* Top Banner Stripe */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '6px',
            background: 'linear-gradient(90deg, #283593, #5c6bc0, #283593)'
          }}></div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <span style={{
                background: '#e8eaf6', color: '#283593', border: '1.2px solid #9fa8da',
                padding: '3px 10px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '800'
              }}>
                🤝 Join Ahobilam Network
              </span>
              <span style={{ fontSize: '26px' }}>📝</span>
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1a237e', marginBottom: '8px', lineHeight: 1.3 }}>
              {language === 'en' ? 'Vadhyar & Service Partner Registration' : 'வாத்தியார் / சேவை பார்ட்னர் பதிவு'}
            </h3>
            <p style={{ fontSize: '0.86rem', color: '#3949ab', lineHeight: 1.5, marginBottom: '14px' }}>
              {language === 'en' ? 'Register to offer your Vaideeka services, catering, or event management through our global platform.' : 'உங்கள் வைதீக சேவைகளை உலகெங்கும் உள்ளவர்களிடம் கொண்டு சேர்க்க இன்றே பதிவு செய்யுங்கள்.'}
            </p>
            <div style={{ background: '#e8eaf6', border: '1px dashed #9fa8da', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#283593', lineHeight: 1.6 }}>
              <div>✓ <b>தகுதிகள்:</b> வைதீக வாத்தியார்கள், சமையல் கலைஞர்கள்</div>
              <div>✓ <b>பயன்கள்:</b> இலவச பதிவு, நேரடி சேவை அழைப்புகள்</div>
            </div>
          </div>
          <a href="/partner-registration" style={{
            background: 'linear-gradient(135deg, #283593 0%, #1a237e 100%)', color: '#ffffff',
            border: '1.5px solid #9fa8da', padding: '10px 16px', borderRadius: '10px', fontWeight: 800,
            fontSize: '13.5px', textDecoration: 'none', textAlign: 'center', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(40, 53, 147, 0.25)',
            transition: 'all 0.2s ease',
          }}>
            <span>{language === 'en' ? 'Register as Partner' : 'பார்ட்னராக பதிவு செய்க'}</span>
            <span>↗</span>
          </a>
        </div>

      </div>
    </div>
  );
};

// MAIN DYNAMIC HOME PAGE COMPONENT
// ============================================================================

// ============================================================================
// UPAKARMA & GAYATHRI JAPAM FESTIVAL SPECIAL SHOWCASE
// ============================================================================
const UpakarmaFestivalBanner = ({ language }) => {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className="scroll-reveal" style={{ padding: '1.2rem 0 0.5rem 0' }}>
      <div style={{
        background: 'linear-gradient(135deg, #4a154b 0%, #6b1d2f 50%, #8b263e 100%)',
        borderRadius: '20px',
        padding: 'clamp(1.2rem, 2.5vw, 2rem)',
        color: '#ffffff',
        boxShadow: '0 12px 35px rgba(107, 29, 47, 0.25)',
        border: '2px solid #d4af37',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Gold Corner Accents */}
        <div style={{
          position: 'absolute', top: '-15px', right: '-15px', width: '90px', height: '90px',
          background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none'
        }} />

        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#fef3c7',
            padding: '5px 16px',
            borderRadius: '25px',
            fontSize: '12.5px',
            fontWeight: 800,
            border: '1px solid rgba(212, 175, 55, 0.5)',
            marginBottom: '10px'
          }}>
            <span>âœ¨</span>
            <span>{language === 'en' ? 'Aavani Avittam Special â€¢ Upakarma 2026' : 'à®†à®µà®£à®¿ à®…à®µà®¿à®Ÿà¯à®Ÿà®®à¯ à®šà®¿à®±à®ªà¯à®ªà¯ â€¢ à®‰à®ªà®¾à®•à®°à¯à®®à®¾ & à®•à®¾à®¯à®¤à¯à®°à®¿ à®œà®ªà®®à¯'}</span>
          </div>

          <h2 style={{
            fontSize: 'clamp(1.3rem, 2.5vw, 2rem)',
            fontWeight: 900,
            color: '#ffffff',
            margin: '0 0 8px 0',
            textShadow: '0 2px 8px rgba(0,0,0,0.4)'
          }}>
            {language === 'en'
              ? 'ðŸ•‰ï¸ Yajur, Bodhayana Upakarma & Gayathri Maha Japam'
              : 'ðŸ•‰ï¸ à®¯à®œà¯à®°à¯, à®ªà¯‹à®¤à®¾à®¯à®© à®‰à®ªà®¾à®•à®°à¯à®®à®¾ & à®•à®¾à®¯à®¤à¯à®°à®¿ à®®à®¹à®¾ à®œà®ª à®ªà®¿à®°à®¯à¯‹à®•à®®à¯'}
          </h2>

          <p style={{
            fontSize: 'clamp(0.85rem, 1.2vw, 0.98rem)',
            color: '#fed7aa',
            maxWidth: '750px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            {language === 'en'
              ? 'Complete authentic procedures, Maha Sankalpam, Kandarishi Tarpanam, Yajnopaveetha Dharana Mantras, and Gayathri 1008/108 Japam.'
              : 'à®ªà¯‚à®£à¯‚à®²à¯ à®®à®¾à®±à¯à®±à¯à®®à¯ à®®à®¨à¯à®¤à®¿à®°à®™à¯à®•à®³à¯, à®®à®¹à®¾ à®šà®™à¯à®•à®²à¯à®ªà®®à¯, à®•à®¾à®£à¯à®Ÿà®°à®¿à®·à®¿ à®¤à®°à¯à®ªà¯à®ªà®£à®®à¯, à®ªà¯à®°à®¹à¯à®®à®¯à®œà¯à®žà®®à¯ à®®à®±à¯à®±à¯à®®à¯ 1008/108 à®•à®¾à®¯à®¤à¯à®°à®¿ à®œà®ª à®®à¯à®´à¯ à®ªà®¿à®°à®¯à¯‹à®• à®•à¯ˆà®¯à¯‡à®Ÿà¯.'}
          </p>
        </div>

        {/* 3 Prominent Featured Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.2rem',
        }}>

          {/* CARD 1: YAJUR UPAKARMA */}
          <div style={{
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            borderTop: '5px solid #d97706',
            transition: 'transform 0.2s ease',
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{
                  background: '#fef3c7', color: '#92400e', padding: '3px 10px',
                  borderRadius: '10px', fontSize: '11.5px', fontWeight: 800
                }}>
                  {language === 'en' ? 'Yajur Veda' : 'à®¯à®œà¯à®°à¯ à®µà¯‡à®¤à®®à¯'}
                </span>
                <span style={{ fontSize: '24px' }}>ðŸ•‰ï¸</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#92400e', marginBottom: '8px' }}>
                {language === 'en' ? 'Yajur Upakarma' : 'à®¯à®œà¯à®°à¯ à®‰à®ªà®¾à®•à®°à¯à®®à®¾'}
              </h3>
              <p style={{ fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.5, marginBottom: '12px' }}>
                {language === 'en'
                  ? 'Yajnopaveetha Dharanam, Kandarishi Tarpanam, Brahmayajnam, and Samidadhanam mantras.'
                  : 'à®ªà¯‚à®£à¯‚à®²à¯ à®®à®¾à®±à¯à®±à¯à®¤à®²à¯, à®•à®¾à®£à¯à®Ÿà®°à®¿à®·à®¿ à®¤à®°à¯à®ªà¯à®ªà®£à®®à¯, à®®à®¹à®¾ à®šà®™à¯à®•à®²à¯à®ªà®®à¯, à®ªà¯à®°à®¹à¯à®®à®¯à®œà¯à®žà®®à¯ & à®šà®®à®¿à®¤à®¾à®¤à®¾à®©à®®à¯.'}
              </p>
            </div>
            <Link
              to="/vaideekam/yajur-upakarma"
              style={{
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 3px 8px rgba(217, 119, 6, 0.3)'
              }}
            >
              <span>{language === 'en' ? 'Open Yajur Prayogam' : 'à®¯à®œà¯à®°à¯ à®ªà®¿à®°à®¯à¯‹à®•à®®à¯ à®ªà®Ÿà®¿à®•à¯à®•'}</span>
              <span>âž”</span>
            </Link>
          </div>

          {/* CARD 2: BODHAYANA UPAKARMA */}
          <div style={{
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            borderTop: '5px solid #16a34a',
            transition: 'transform 0.2s ease',
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{
                  background: '#dcfce7', color: '#166534', padding: '3px 10px',
                  borderRadius: '10px', fontSize: '11.5px', fontWeight: 800
                }}>
                  {language === 'en' ? 'Bodhayana Sutram' : 'à®ªà¯‹à®¤à®¾à®¯à®© à®šà¯‚à®¤à¯à®°à®®à¯'}
                </span>
                <span style={{ fontSize: '24px' }}>ðŸ“œ</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#166534', marginBottom: '8px' }}>
                {language === 'en' ? 'Bodhayana Upakarma' : 'à®ªà¯‹à®¤à®¾à®¯à®© à®‰à®ªà®¾à®•à®°à¯à®®à®¾'}
              </h3>
              <p style={{ fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.5, marginBottom: '12px' }}>
                {language === 'en'
                  ? 'Bodhayana Maha Sankalpam, Bodhayana Brahmayajnam, and specific Kandarishi Tarpanam.'
                  : 'à®ªà¯‹à®¤à®¾à®¯à®© à®®à®¹à®¾ à®šà®™à¯à®•à®²à¯à®ªà®®à¯, à®ªà¯‹à®¤à®¾à®¯à®© à®ªà¯à®°à®¹à¯à®®à®¯à®œà¯à®žà®®à¯ à®®à®±à¯à®±à¯à®®à¯ à®µà®¿à®šà¯‡à®· à®•à®¾à®£à¯à®Ÿà®°à®¿à®·à®¿ à®¤à®°à¯à®ªà¯à®ªà®£ à®®à¯à®±à¯ˆà®•à®³à¯.'}
              </p>
            </div>
            <Link
              to="/vaideekam/bodhayana-upakarma"
              style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 3px 8px rgba(22, 163, 74, 0.3)'
              }}
            >
              <span>{language === 'en' ? 'Open Bodhayana Prayogam' : 'à®ªà¯‹à®¤à®¾à®¯à®© à®ªà®¿à®°à®¯à¯‹à®•à®®à¯ à®ªà®Ÿà®¿à®•à¯à®•'}</span>
              <span>âž”</span>
            </Link>
          </div>

          {/* CARD 3: GAYATHRI JAPAM */}
          <div style={{
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            borderTop: '5px solid #7c3aed',
            transition: 'transform 0.2s ease',
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{
                  background: '#f3e8ff', color: '#6b21a8', padding: '3px 10px',
                  borderRadius: '10px', fontSize: '11.5px', fontWeight: 800
                }}>
                  {language === 'en' ? 'Gayathri Japam' : 'à®•à®¾à®¯à®¤à¯à®°à®¿ à®®à®¹à®¾ à®œà®ªà®®à¯'}
                </span>
                <span style={{ fontSize: '24px' }}>ðŸ“¿</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#6b21a8', marginBottom: '8px' }}>
                {language === 'en' ? 'Gayathri Maha Japam' : 'à®•à®¾à®¯à®¤à¯à®°à®¿ à®®à®¹à®¾ à®œà®ªà®®à¯'}
              </h3>
              <p style={{ fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.5, marginBottom: '12px' }}>
                {language === 'en'
                  ? 'Gayathri Avahanam, Nyasam, 1008/108 Japam Sankalpam, Upasthanam, and Dikhvandanam.'
                  : 'à®•à®¾à®¯à®¤à¯à®°à®¿ à®†à®µà®¾à®¹à®©à®®à¯, à®¨à¯à®¯à®¾à®¸à®®à¯, 1008/108 à®œà®ª à®šà®™à¯à®•à®²à¯à®ªà®®à¯, à®‰à®ªà®¸à¯à®¤à®¾à®©à®®à¯ à®®à®±à¯à®±à¯à®®à¯ à®¤à®¿à®•à¯à®µà®¨à¯à®¤à®©à®®à¯.'}
              </p>
            </div>
            <Link
              to="/vaideekam/gayathrijapam"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 3px 8px rgba(124, 58, 237, 0.3)'
              }}
            >
              <span>{language === 'en' ? 'Open Gayathri Japam' : 'à®•à®¾à®¯à®¤à¯à®°à®¿ à®œà®ªà®®à¯ à®ªà®Ÿà®¿à®•à¯à®•'}</span>
              <span>âž”</span>
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

const DynamicHomePage = ({ language, user, navigate, CommentSection, ForgotPasswordModal }) => {
  const theme = getTodayTheme();
  const [qaStats, setQaStats] = useState({ total: '3400+' });
  const [recentQa, setRecentQa] = useState([]);
  const [publicStats, setPublicStats] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Fetch data
  useEffect(() => {
    // Q&A stats
    axios.get(`${API}/qa/list?limit=30`)
      .then(res => {
        if (res.data) {
          if (res.data.total) setQaStats({ total: res.data.total });
          if (res.data.items) setRecentQa(res.data.items);
          else if (res.data.questions) setRecentQa(res.data.questions);
        }
      })
      .catch(() => {});

    // Public stats
    axios.get(`${API}/analytics/public-stats`)
      .then(res => setPublicStats(res.data))
      .catch(() => {});

    // Blog posts (for content sections)
    axios.get(`${API}/blog/posts?limit=20`)
      .then(res => setBlogPosts(res.data || []))
      .catch(() => {});

    // Admin redirect
    const params = new URLSearchParams(window.location.search);
    if ((params.get('admin') === 'true' || localStorage.getItem('redirectToAdmin') === 'true')
        && user && (user.role === 'admin' || user.role === 'moderator')) {
      localStorage.removeItem('redirectToAdmin');
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  // Forgot password event
  useEffect(() => {
    const handleFP = () => setShowForgotPassword(true);
    window.addEventListener('openForgotPassword', handleFP);
    return () => window.removeEventListener('openForgotPassword', handleFP);
  }, []);

  return (
    <div className={`min-h-screen ${theme.className}`} style={{ background: 'var(--day-bg-gradient, #f9fafb)' }}>

      {/* Hero Section */}
      <HeroSection theme={theme} language={language} />

      {/* Upakarma & Gayathri Japam Festive Special Showcase */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <UpakarmaFestivalBanner language={language} />
      </div>

      {/* Premier Featured Showcase: Panchangam & 6,500+ Shastra Decision Rules */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <PremierFeaturedShowcase language={language} />
      </div>

      {/* Main Category Sections â€” Immediately Below Hero */}
      <div style={{ maxWidth: '1200px', margin: '1rem auto 0', padding: '0 1rem' }}>
        {CATEGORY_SECTIONS.map((section, index) => (
          <React.Fragment key={section.id}>
            <CategorySection
              section={section}
              language={language}
              posts={blogPosts.filter(p =>
                (p.categories || []).some(c =>
                  (typeof c === 'string' ? c : c.name || '').toLowerCase().includes(section.id.toLowerCase())
                )
              )}
              isAlternate={index % 2 === 1}
            />
            {index < CATEGORY_SECTIONS.length - 1 && (
              <div className="section-divider" style={{ margin: '0 auto' }}></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Section Divider */}
      <div className="section-divider" style={{ margin: '1rem auto' }}></div>

      {/* Q&A Highlight */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <QAHighlightSection recentQa={recentQa} qaStats={qaStats} language={language} navigate={navigate} />
      </div>

      {/* Section Divider */}
      <div className="section-divider" style={{ margin: '1rem auto' }}></div>

      {/* YouTube */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <YouTubeSection language={language} />
      </div>

      {/* Section Divider */}
      <div className="section-divider" style={{ margin: '1rem auto' }}></div>

      {/* Photo Gallery */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <PhotoGallery language={language} />
      </div>

      {/* About Section */}
      <div className="kolam-pattern" style={{ background: 'var(--day-bg-gradient, #f0fdf4)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', position: 'relative', zIndex: 1 }}>
          <AboutSection language={language} />
        </div>
      </div>

      {/* Comments */}
      {CommentSection && (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
          <CommentSection pageSlug="home" />
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && ForgotPasswordModal && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>
  );
};

export default DynamicHomePage;
export { getTodayTheme, DAY_THEMES };

